<template>
  <div class="search-component">
    <div class="search-component__input-box">
      <CInput
        v-if="selectedOption === 'userDetails'"
        v-model="searchTermForUser"
        :placeholder="getPlaceholderText"
        :debounce="1000"
        class="search-component__input"
        @update:value="performSearch"
      />

      <CInput
        v-if="selectedOption === 'companyName'"
        v-model="searchTermForCompany"
        :placeholder="getPlaceholderText"
        :debounce="1000"
        class="search-component__input"
        @update:value="performSearch"
      />

      <div class="search-component__tags">
        <button
          v-for="(option, index) in searchOptions"
          :key="index"
          class="btn search-component__tag"
          :class="{
            'search-component__tag--active': selectedOption === option.value,
            'btn-primary': selectedOption === option.value,
            'btn-outline-primary': selectedOption !== option.value
          }"
          @click="selectSearchOption(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <CSpinner
      v-if="isLoading"
      size="sm"
    />

    <div
      v-if="companyNameSearchResults.length && selectedOption === 'companyName'"
      class="search-component__results"
    >
      <div 
        v-for="(result, index) in companyNameSearchResults" 
        :key="index" 
      >
        <c-card>
          <c-card-header class="search-component__card-header">
            {{ result.companyName }}

            <CButton
              v-if="result.ringbaAccountId"
              color="secondary"
              size="sm"
              class="ml-2"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              Copy Ringba Account ID
              <CIcon
                name="cil-copy"
                size="custom"
                class="ml-1"
              />
            </CButton>
          </c-card-header>

          <c-card-body>
            <p
              class="card-text"
            >
              <strong>Buyer Account:</strong>
              <CButton
                v-if="result.buyerAccountId"
                color="primary"
                size="sm"
                class="ml-2"
                @click="$emit('account-selected', result.ringbaAccountId)"
              >
                Filter Charts
                <CIcon
                  name="cil-filter"
                  size="custom"
                  class="ml-1"
                />
              </CButton>
              <CButton
                v-if="result.buyerAccountId"
                color="info"
                size="sm"
                class="ml-2"
                target="_blank"
                :to="{
                  name: 'BuyerAccountDetails',
                  params: { id: result.buyerAccountId },
                  exact: true,
                }"
              >
                View Account
                <CIcon
                  name="cil-external-link"
                  size="custom"
                  class="ml-1"
                />
              </CButton>
              <span
                v-else
                class="ml-2"
              >N/A</span>
            </p>
            <p
              class="card-text"
            >
              <strong>Seller Account:</strong>
              <CButton
                v-if="result.sellerAccountId"
                color="info"
                size="sm"
                class="ml-2"
                target="_blank"
                :to="{
                  name: 'SellerAccountDetails',
                  params: { id: result.sellerAccountId },
                  exact: true,
                }"
              >
                View Account
                <CIcon
                  name="cil-external-link"
                  size="custom"
                  class="ml-1"
                />
              </CButton>
              <span
                v-else
                class="ml-2"
              >N/A</span>
            </p>
          </c-card-body>

          <c-card-footer>
            <p
              class="card-text"
            >
              <strong>Seller Application:</strong>
              <CButton
                v-if="result.sellerRegistrationId"
                color="info"
                size="sm"
                class="ml-2"
                target="_blank"
                :to="{
                  name: 'RegistrationRequestDetails',
                  params: { id: result.sellerRegistrationId, type: 'seller' },
                  exact: true,
                }"
              >
                View Application
                <CIcon
                  name="cil-external-link"
                  size="custom"
                  class="ml-1"
                />
              </CButton>
              <span
                v-else
                class="ml-2"
              >N/A</span>
            </p>
            <p
              class="card-text"
            >
              <strong>Buyer Application:</strong>
              <CButton
                v-if="result.buyerRegistrationId"
                color="info"
                size="sm"
                class="ml-2"
                target="_blank"
                :to="{
                  name: 'RegistrationRequestDetails',
                  params: { id: result.buyerRegistrationId, type: 'buyer' },
                  exact: true,
                }"
              >
                View Application
                <CIcon
                  name="cil-external-link"
                  size="custom"
                  class="ml-1"
                />
              </CButton>
              <span
                v-else
                class="ml-2"
              >N/A</span>
            </p>
          </c-card-footer>
        </c-card>
      </div>
    </div>

    <div
      v-if="accountsSearchByUserResults.buyers.length && selectedOption === 'userDetails'"
      class="search-component__results"
    >
      <h4>Buyer Accounts</h4>
      <div 
        v-for="(result, index) in accountsSearchByUserResults.buyers" 
        :key="index" 
      >
        <c-card v-if="result">
          <c-card-header class="search-component__card-header">
            <router-link
              :to="{ name: 'BuyerAccountDetails', params: { id: result.id }, exact: true }"
              target="_blank"
            >
              {{ result.name }}

              <CIcon
                name="cil-external-link"
                size="custom"
                class="ml-1"
              />
            </router-link>

            <CButton
              v-if="result.ringbaAccountId"
              color="secondary"
              size="sm"
              class="ml-2"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              Copy Ringba Account ID
              <CIcon
                name="cil-copy"
                size="custom"
                class="ml-1"
              />
            </CButton>
          </c-card-header>
        </c-card>
      </div>
    </div>

    <div
      v-if="accountsSearchByUserResults.sellers.length && selectedOption === 'userDetails'"
      class="search-component__results"
    >
      <h4>Seller Accounts</h4>
      <div 
        v-for="(result, index) in accountsSearchByUserResults.sellers" 
        :key="index" 
      >
        <c-card v-if="result">
          <c-card-header class="search-component__card-header">
            <router-link
              :to="{ name: 'SellerAccountDetails', params: { id: result.id }, exact: true }"
              target="_blank"
            >
              {{ result.name }}

              <CIcon
                name="cil-external-link"
                size="custom"
                class="ml-1"
              />
            </router-link>

            <CButton
              v-if="result.ringbaAccountId"
              color="secondary"
              size="sm"
              class="ml-2"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              Copy Ringba Account ID
              <CIcon
                name="cil-copy"
                size="custom"
                class="ml-1"
              />
            </CButton>
          </c-card-header>
        </c-card>
      </div>
    </div>

    <div
      v-if="buyerRegistrationRequestsForUser.length && selectedOption === 'userDetails'"
      class="search-component__results"
    >
      <h4>Buyer Applications</h4>
      <div 
        v-for="(result, index) in buyerRegistrationRequestsForUser" 
        :key="index" 
      >
        <c-card>
          <c-card-header class="search-component__card-header">
            <router-link
              :to="{ name: 'RegistrationRequestDetails', params: { id: result.id, type: 'buyer' }, exact: true }"
              target="_blank"
            >
              {{ result.companyName }}

              <CIcon
                name="cil-external-link"
                size="custom"
                class="ml-1"
              />
            </router-link>

            <CButton
              v-if="result.ringbaAccountId"
              color="secondary"
              size="sm"
              class="ml-2"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              Copy Ringba Account ID
              <CIcon
                name="cil-copy"
                size="custom"
                class="ml-1"
              />
            </CButton>
          </c-card-header>
        </c-card>
      </div>
    </div>

    <div
      v-if="sellerRegistrationRequestsForUser.length && selectedOption === 'userDetails'"
      class="search-component__results"
    >
      <h4>Seller Applications</h4>
      <div 
        v-for="(result, index) in sellerRegistrationRequestsForUser" 
        :key="index" 
      >
        <c-card>
          <c-card-header class="search-component__card-header">
            <router-link
              :to="{ name: 'RegistrationRequestDetails', params: { id: result.id, type: 'seller' }, exact: true }"
              target="_blank"
            >
              {{ result.companyName }}

              <CIcon
                name="cil-external-link"
                size="custom"
                class="ml-1"
              />
            </router-link>

            <CButton
              v-if="result.ringbaAccountId"
              color="secondary"
              size="sm"
              class="ml-2"
              @click="copyAccountIdToClipboard(result.ringbaAccountId)"
            >
              Copy Ringba Account ID
              <CIcon
                name="cil-copy"
                size="custom"
                class="ml-1"
              />
            </CButton>
          </c-card-header>
        </c-card>
      </div>
    </div>

    <div 
      v-if="noResultsFound"
      class="search-component__no-results text-center text-muted mt-3"
    >
      No results found
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { showSuccessMessage } from '@/notification-utils'

export default {
  name: 'SearchAccountOrUser',

  mixins: [copyToClipboardMixin],

  data() {
    return {
      isLoading: false,
      searchTermForUser: '',
      searchTermForCompany: '',
      searched: false,
      previousSearchTerm: '',
      selectedOption: 'companyName',
      searchOptions: [
        { value: 'companyName', label: 'Company Name' },
        { value: 'userDetails', label: 'User Details' }
      ]
    }
  },

  computed: {
    ...mapState('accounts', [
      'companyNameSearchResults',
      'accountsSearchByUserResults'
    ]),

    ...mapState('registrationRequests', [
      'buyerRegistrationRequests',
      'sellerRegistrationRequests'
    ]),

    getPlaceholderText() {
      return this.selectedOption === 'companyName'
        ? 'Search by Company Name'
        : 'Search by User Name or Email'
    },

    buyerRegistrationRequestsForUser() {
      if (this.selectedOption === 'userDetails' && this.searchTermForUser != '') {
        return this.buyerRegistrationRequests.filter(req => {
          return (req.status.toLowerCase() === 'waitlisted' || req.status.toLowerCase() === 'pending')
                && (`${req.representative.firstName.toLowerCase()} ${req.representative.lastName.toLowerCase()}`.includes(this.searchTermForUser.toLowerCase()) ||
                req.representative.email.toLowerCase().includes(this.searchTermForUser.toLowerCase()))
        })
      }

      return []
    },

    sellerRegistrationRequestsForUser() {
      if (this.selectedOption === 'userDetails' && this.searchTermForUser != '') {
        return this.sellerRegistrationRequests.filter(req => {
          return `${req.representative.firstName.toLowerCase()} ${req.representative.lastName.toLowerCase()}`.includes(this.searchTermForUser.toLowerCase()) ||
            req.representative.email.toLowerCase().includes(this.searchTermForUser.toLowerCase())
        })
      }

      return []
    },

    noResultsFound() {
      return this.searched && (this.isUserSearchResultEmpty || this.isCompanyNameSearchResultEmpty)
    },

    isUserSearchResultEmpty() {
      return this.selectedOption === 'userDetails' && !this.sellerRegistrationRequestsForUser && !this.buyerRegistrationRequestsForUser && !this.accountsSearchByUserResults.buyers.length && !this.accountsSearchByUserResults.sellers.length
    },

    isCompanyNameSearchResultEmpty() {
      return this.selectedOption === 'companyName' && !this.companyNameSearchResults.length
    }
  },

  methods: {
    ...mapActions('accounts', [
      'searchByCompanyName',
      'searchAccountsByUser'
    ]),

    ...mapActions('registrationRequests', [
      'getRegistrationRequests',
    ]),

    copyAccountIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Ringba Account ID copied to clipboard')
    },

    selectSearchOption(option) {
      this.selectedOption = option
      this.searched = false
    },
    
    async performSearch() {
      try {
        this.isLoading = true
        
        if (this.selectedOption === 'companyName') {
          if (!this.searchTermForCompany.trim() || this.searchTermForCompany.trim() === this.previousSearchTerm) {
            return
          }

          await this.searchByCompanyName(this.searchTermForCompany.trim())

          this.previousSearchTerm = this.searchTermForCompany.trim()
        } else {
          if (!this.searchTermForUser.trim() || this.searchTermForUser.trim() === this.previousSearchTerm) {
            return
          }

          await this.searchAccountsByUser(this.searchTermForUser.trim())

          if (!this.sellerRegistrationRequests.length || !this.buyerRegistrationRequests.length) {
            await this.getRegistrationRequests({ registrationType: 'buyer', })
            await this.getRegistrationRequests({ registrationType: 'seller', })
          }

          this.previousSearchTerm = this.searchTermForUser.trim()
        }

        this.searched = true
      } catch (err) {
        //
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.search-component {
  position: relative;
  
  &__input-box {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8f9fa;
    padding: 8px 0;
  }

  &__input-container {
    margin-bottom: 1rem;
  }

  &__tags {
    display: flex;
    gap: 0.5rem;
  }

  &__tag {
    &--active {
      font-weight: bold;
    }
  }

  &__results {
    margin-top: 1rem;
  }

  &__card-header {
    font-weight: bold;
    background-color: #f0f3f5;
  }

  &__error {
    margin-top: 1rem;
    text-align: center;
  }

  &__no-results {
    font-style: italic;
  }
}
</style>